import React, { useEffect, useMemo, useRef } from 'react';
import { MultiSelect } from '@progress/kendo-react-dropdowns';
import PropTypes from 'prop-types';
import { useValidation } from '../utils/ValidationProvider';
import './Multiselect.scss';

/**
 * Multiselect Component
 * @module
 * @param {array} data - Data
 * @param {string} textField - Text field
 * @param {array} [value] - Current Value
 * @param {func} onChange - Function invoked on value change
 * @param {bool} [required] - Is field required?
 * @param {string} name - Name
 * @param {string} [validationMessage] - Validation message if field is invalid
 * @param {bool} [disabled] - Is multiselect readonly?
 * @param {number} [tabIndex] - Multiselect tabindex
 * @param {func} [onFilterChange] - Function invoked if filter changes
 * @param {string} [placeholder] - Placeholder
 * @param {string} [filter] - Filter
 */

const MultiselectComponent = (props) => {
  const field = useMemo(() => ({
    field: props.name,
    title: props.title,
    type: props.type,
    min: props.min,
    max: props.max,
    minLength: props.minLength,
    maxLength: props.maxLength,
    validationMessage: props.validationMessage,
    pattern: props.pattern,
    required: props.required,
    disabled: props.disabled,
    recommended: props.recommended,
    validationAllowed: props.validationAllowed,
    isInvalid: props.isInvalid
  }), [props.disabled, props.max, props.maxLength, props.min, props.minLength, props.name, props.pattern, props.required, props.title, props.type, props.validationMessage, props.recommended, props.validationAllowed, props.isInvalid]);

  const { validate, setValidationValue } = useValidation(field, props.value);
  const setFormValidationValue = props.setValidation || setValidationValue;
  const dropDownRef = useRef(null)

  useEffect(() => {
    if (dropDownRef.current && dropDownRef.current.element && props.name) {
      dropDownRef.current.element.setAttribute('data-testid', props.name);
    }
  }, [props.name])

  const onBlur = () => {
    props.validationAllowed && validate();
    if (props.onBlur) {
      props.onBlur();
    }
  };

  const onFilterChange = (event) => {
    let selectedWithInputValue = [
      event.target.value,
      ...(event.filter.value !== "" ? [event.filter.value] : [])
    ];

    if (selectedWithInputValue.length === 1 && selectedWithInputValue[0].length === 0) {
      selectedWithInputValue = null;
    }

    setFormValidationValue(selectedWithInputValue);
    if (props.onFilterChange) {
      props.onFilterChange({ filters: [event.filter] }, null, null, event.filter.value);
    }
  };

  /**
   * Metoda buildFooter
   * vráti element, ktorý sa má vyrenderovať pod dropdownlistom
   * default zobrazuje počet zobrazených záznamov a celkovy počet záznamov
   */
  const buildFooter = () => {
    if (props.total) {
      let text = props.data && props.data.length > 0 ? `Zobrazených ${props.data.length} z ${props.total} záznamov` : "";
      return (
        <div className="autocomplete-footer" title="Pre zobrazenie nezobrazených výsledkov spresnite podmienku vyhľadávania alebo použite dialóg.">
          <div className="autocomplete-footer-text">
            {text}
          </div>
        </div>
      );
    } else {
      return null; // TODO: preco sa tu vracal 'undefined'?
    }
  };

  return (
    <MultiSelect
      ref={dropDownRef}
      className={`form-input multiselect ${props.disabled ? "disabled-field" : props.required ? "required-field" : (props.recommended ? "recommended-field" : "default-field")}`}
      data={props.data}
      onChange={e => {
        props.onChange(props.name, e.target.value);
        setFormValidationValue(e.target.value);
        if (e.nativeEvent.srcElement.nodeName !== "svg" && e.nativeEvent.srcElement.nodeName !== "SPAN") validate();
      }}
      value={props.value}
      textField={props.textField}
      dataItemKey={props.textField}
      disabled={props.disabled}
      required={props.required}
      validationMessage={props.validationMessage || 'Povinné'}
      tabIndex={props.tabIndex}
      onBlur={onBlur}
      filterable={props.onFilterChange !== undefined}
      onFilterChange={onFilterChange}
      placeholder={props.placeholder}
      filter={props.filter}
      footer={buildFooter()} // footer zobrazený pod dropdownListom, zobrazuje sa iba ak je naplnená props total
    />
  );
};

MultiselectComponent.propTypes = {
  data: PropTypes.array.isRequired,
  total: PropTypes.number,
  textField: PropTypes.string.isRequired,
  value: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  required: PropTypes.bool,
  name: PropTypes.string.isRequired,
  validationMessage: PropTypes.string,
  disabled: PropTypes.bool,
  tabIndex: PropTypes.number,
  onFilterChange: PropTypes.func,
  placeholder: PropTypes.string,
  filter: PropTypes.string
}

export default MultiselectComponent;